import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Search, Link2 } from 'lucide-react';

// Endpoints por tipo de integração (novos painéis entram aqui)
const SYNC_API = {
  sigma_login: {
    scan: (id) => `/sigma/login/${id}/link-scan`,
    job: (jobId) => `/sigma/login/link-jobs/${jobId}`,
    apply: '/sigma/login/link-apply',
  },
};

export default function SyncCustomersCard({ api }) {
  const [sources, setSources] = useState([]);
  const [sourceKey, setSourceKey] = useState('');
  const [job, setJob] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [syncPlanId, setSyncPlanId] = useState('');

  useEffect(() => {
    api.get('/imports/sources').then((r) => {
      const list = (r.data.sources || []).filter((s) => SYNC_API[s.type]);
      setSources(list);
      if (list.length) setSourceKey((prev) => (prev && list.some((s) => `${s.type}:${s.id}` === prev) ? prev : `${list[0].type}:${list[0].id}`));
    }).catch(() => {});
  }, [api]);

  useEffect(() => {
    api.get('/customer-plans').then((r) => setPlans(r.data || [])).catch(() => {});
  }, [api]);

  const source = sources.find((s) => `${s.type}:${s.id}` === sourceKey);
  const isPanelFoda = source?.provider === 'painelfoda';
  const plansForSource = plans.filter((p) => p.active !== false && p.sigma_login_integration_id === source?.id);

  const poll = async (endpoint) => {
    for (let i = 0; i < 180; i++) {
      try {
        const r = await api.get(endpoint);
        if (r.data.status !== 'running') return r.data;
      } catch (e) {
        return { status: 'error', error: e.response?.data?.detail || 'Erro ao acompanhar a sincronização' };
      }
      await new Promise((ok) => setTimeout(ok, 4000));
    }
    return { status: 'error', error: 'A sincronização demorou demais. Tente novamente.' };
  };

  const scan = async () => {
    if (!source) { toast.error('Escolha a integração'); return; }
    const cfg = SYNC_API[source.type];
    try {
      setBusy(true);
      setJob({ status: 'running' });
      const r = await api.post(cfg.scan(source.id));
      const result = await poll(cfg.job(r.data.job_id));
      setJob({ ...result, source });
      if (result.status === 'error') { toast.error(result.error || 'Erro na sincronização'); return; }
      const sel = {};
      (result.proposals || []).forEach((p) => { if (p.recommended) sel[p.customer_id] = true; });
      setSelected(sel);
      if (!(result.proposals || []).length) toast.info('Nenhum cliente seu foi encontrado nessa integração');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar a sincronização');
      setJob(null);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error('Selecione ao menos um cliente'); return; }
    try {
      setBusy(true);
      const body = { job_id: job.id, customer_ids: ids };
      if (job.source?.provider === 'painelfoda' && syncPlanId) body.plan_id = syncPlanId;
      const r = await api.post(SYNC_API[job.source.type].apply, body);
      toast.success(r.data.message);
      (r.data.skipped || []).slice(0, 5).forEach((m) => toast.warning(m));
      setJob(null);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setBusy(false);
    }
  };

  const proposals = job?.proposals || [];
  const selectable = proposals.filter((p) => !p.conflict);

  return (
    <Card data-testid="sync-customers-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle>Sincronizar clientes</CardTitle>
            <CardDescription>
              Liga os clientes que você já tem cadastrados aos clientes do painel. Atualiza só o usuário do painel e o vencimento — plano, preço e o resto ficam como estão.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">Conecte uma integração acima para sincronizar seus clientes.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Integração</Label>
              <Select value={sourceKey} onValueChange={(v) => { setSourceKey(v); setJob(null); setSelected({}); }} disabled={busy}>
                <SelectTrigger data-testid="sync-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={scan} disabled={busy || !source} data-testid="sync-scan-btn">
              {busy && job?.status === 'running' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Procurar clientes
            </Button>
          </div>
        )}

        {job?.status === 'running' && (
          <p className="text-xs text-muted-foreground">Lendo os clientes do painel… com muitos clientes pode levar alguns minutos. Pode continuar usando o sistema.</p>
        )}

        {job?.status === 'done' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {job.stats?.panel_total} clientes no painel{job.stats?.hidden_other_resellers > 0 && ` (${job.stats.hidden_other_resellers} das suas revendas ignorados)`} · {job.stats?.customers_unlinked} seus ainda sem vínculo · <b>{job.stats?.matches}</b> encontrado(s)
              {job.stats?.ambiguous_phone > 0 && ` · ${job.stats.ambiguous_phone} com telefone repetido no painel (ignorados)`}
            </p>
            {proposals.length > 0 && (
              <>
                <div className="rounded-lg border overflow-x-auto max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectable.length > 0 && selectable.every((p) => selected[p.customer_id])}
                            onCheckedChange={(checked) => { const s = {}; selectable.forEach((p) => { s[p.customer_id] = checked; }); setSelected(s); }}
                          />
                        </TableHead>
                        <TableHead>Seu cliente</TableHead>
                        <TableHead>No painel</TableHead>
                        <TableHead>Encontrado por</TableHead>
                        <TableHead>Vencimento</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proposals.map((p) => (
                        <TableRow key={p.customer_id} className={p.conflict ? 'opacity-60' : ''}>
                          <TableCell>
                            <Checkbox disabled={p.conflict} checked={!!selected[p.customer_id]}
                              onCheckedChange={(checked) => setSelected((prev) => ({ ...prev, [p.customer_id]: checked }))} />
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-sm">{p.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{p.customer_phone || '—'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="font-mono text-sm">{p.panel.username}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{p.panel.package_name}{p.panel.is_test ? ' · teste' : ''}</p>
                          </TableCell>
                          <TableCell>
                            {p.conflict
                              ? <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">Conflito</Badge>
                              : <Badge variant="outline" className={`text-[10px] ${p.reason === 'Nome' ? 'text-amber-500 border-amber-500/30' : 'text-emerald-500 border-emerald-500/30'}`}>{p.reason}</Badge>}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            <span className="text-muted-foreground">{p.current_expiration || '—'}</span> → <b>{p.panel.expiration_date || '—'}</b>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Nada é alterado até você confirmar. Encontrados só pelo <b>nome</b> vêm desmarcados: confira antes. "Conflito" = o mesmo cliente do painel bateu com mais de um cliente seu.
                </p>
                {job.source?.provider === 'painelfoda' && (
                  <div className="space-y-1.5 rounded-lg border border-border/60 p-3 bg-muted/20">
                    <Label className="text-xs">Plano a aplicar (pacote do painel)</Label>
                    {plansForSource.length > 0 ? (
                      <Select value={syncPlanId} onValueChange={setSyncPlanId}>
                        <SelectTrigger><SelectValue placeholder="Escolha o plano destes clientes" /></SelectTrigger>
                        <SelectContent>
                          {plansForSource.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name} — R$ {Number(p.price || 0).toFixed(2)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[11px] text-amber-500">Nenhum plano deste painel ainda. Crie um em Planos → Novo plano (vinculado a este painel) antes de sincronizar.</p>
                    )}
                    <p className="text-[11px] text-muted-foreground">O plano define o pacote e as telas usados na renovação. Não altera nada no painel.</p>
                  </div>
                )}
                <Button onClick={apply} disabled={busy || (job.source?.provider === 'painelfoda' && !syncPlanId)} className="w-full" data-testid="sync-apply-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Sincronizar selecionados ({Object.values(selected).filter(Boolean).length})
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
